import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, ShieldCheckIcon, ClockIcon, HeartIcon, ArrowRightIcon, StarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export function Hero() {
  const features = [{
    icon: <ShieldCheckIcon className="h-4 w-4" data-id="element-98" />,
    text: 'Evidence-Based'
  }, {
    icon: <ClockIcon className="h-4 w-4" data-id="element-99" />,
    text: '24/7 Availability'
  }, {
    icon: <HeartIcon className="h-4 w-4" data-id="element-100" />,
    text: 'Holistic Care'
  }];
  return <section className="relative min-h-[85vh] flex flex-col lg:flex-row bg-brand-navy overflow-hidden" data-id="element-101">
      {/* Left Side: Content (55%) */}
      <div className="w-full lg:w-[55%] flex items-center z-10 py-16 lg:py-24" data-id="element-102">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 xl:px-20" data-id="element-103">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-104">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 px-3 py-1 rounded-full mb-10 backdrop-blur-md" data-id="element-105">
              <div className="flex -space-x-1" data-id="element-106">
                {[1, 2, 3].map(i => <div key={i} className="w-5 h-5 rounded-full border border-brand-navy bg-gray-400 overflow-hidden" data-id="element-107">
                    <img src={`https://i.pravatar.cc/100?img=${i + 20}`} alt="User" data-id="element-108" />
                  </div>)}
              </div>
              <div className="flex items-center text-[10px] uppercase tracking-widest font-bold text-white/70" data-id="element-109">
                <StarIcon className="h-3 w-3 text-brand-red mr-1 fill-brand-red" data-id="element-110" />
                Trusted by 500+ Families
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight mb-6" data-id="element-111">
              Restoring <span className="text-brand-red" data-id="element-112">Health.</span>
              <br data-id="element-113" />
              Renewing <span className="text-brand-red" data-id="element-114">Minds.</span>
              <br data-id="element-115" />
              Rebuilding <span className="text-brand-red" data-id="element-116">Lives.</span>
            </h1>

            <p className="text-base md:text-lg text-white/70 max-w-lg leading-relaxed mb-10 font-medium" data-id="element-117">
              Global Restorative Health & Wellness LLC integrates primary
              healthcare and mental wellness to help you regain control and
              rebuild resilience.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 mb-12" data-id="element-118">
              <Link to="/contact" className="w-full sm:w-auto bg-brand-red text-white px-8 py-4 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-white hover:text-brand-navy transition-all shadow-xl shadow-brand-red/10 flex items-center justify-center group" data-id="element-119">
                <CalendarIcon className="mr-2 h-4 w-4" data-id="element-120" />
                Book Consultation
              </Link>
              <Link to="/services" className="w-full sm:w-auto bg-white/5 text-white border border-white/20 px-8 py-4 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-white hover:text-brand-navy transition-all backdrop-blur-md flex items-center justify-center" data-id="element-121">
                Our Services
                <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-122" />
              </Link>
            </div>

            {/* Feature Bar */}
            <div className="flex flex-wrap items-center gap-y-4 gap-x-8 pt-10 border-t border-white/10" data-id="element-123">
              {features.map((feature, index) => <div key={index} className="flex items-center space-x-2 text-white/50" data-id="element-124">
                  <div className="text-brand-red" data-id="element-125">{feature.icon}</div>
                  <span className="text-[10px] font-bold uppercase tracking-widest" data-id="element-126">
                    {feature.text}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side: Image (45%) */}
      <div className="relative w-full lg:w-[45%] min-h-[400px] lg:min-h-full overflow-hidden" data-id="element-127">
        <motion.img initial={{
        scale: 1.1,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 1.2
      }} src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=1200" alt="Healthcare professional" className="absolute inset-0 w-full h-full object-cover" data-id="element-128" />
        {/* Subtle Overlay to blend with Navy */}
        <div className="absolute inset-0 bg-brand-navy/20 lg:bg-gradient-to-r lg:from-brand-navy lg:to-transparent" data-id="element-129"></div>

        {/* Floating Stat Card */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="absolute bottom-10 right-10 bg-white/10 backdrop-blur-xl border border-white/20 p-6 rounded-2xl hidden xl:block" data-id="element-130">
          <div className="flex items-center space-x-4" data-id="element-131">
            <div className="bg-brand-red/20 p-3 rounded-xl" data-id="element-132">
              <HeartIcon className="h-6 w-6 text-brand-red" data-id="element-133" />
            </div>
            <div data-id="element-134">
              <p className="text-[10px] text-white/60 font-bold uppercase tracking-wider" data-id="element-135">
                Total Wellness
              </p>
              <p className="text-lg font-black text-white" data-id="element-136">Integrated Care</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}